import React, { useState, useEffect, useMemo } from "react";
import { Dropdown, DropdownMenu, DropdownToggle, Button } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocationQuery } from "../../../../base/hooks/useQueryString";
import TableSearch from "../../../../base/components/Table/tableSearch";
import { FilterListWithInfinityScroll } from "../../../../base/components/FilterListWithInfinityScroll";
import styles from './HistoryNotifications.module.scss';

export function formatDateDDMMYYYY(dateObj) {
  if (!dateObj) return '';
  const date = new Date(dateObj);
  if (isNaN(date)) return '';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function formatDateRangeDisplay(startDate, endDate) {
  if (!startDate || !endDate) return '';
  
  const displayStartDate = new Date(startDate);
  displayStartDate.setHours(0, 0, 0, 0);
  
  const displayEndDate = new Date(endDate);
  displayEndDate.setHours(23, 59, 59, 999);
  
  const startFormatted = formatDateDDMMYYYY(displayStartDate);
  const endFormatted = formatDateDDMMYYYY(displayEndDate);
  
  return `${startFormatted} - ${endFormatted}`;
}

export const TableFilter = ({ filterProvider, notificationTypes = [] }) => {
  const { type, dateRange } = filterProvider;
  const [isOpen, setIsOpen] = useState(false);
  const [typeValue, setTypeValue] = useState(() => {
    const value = type.getValue();
    if (typeof value === 'string') {
      return value.split(',').map(t => t.trim()).filter(Boolean);
    }
    return Array.isArray(value) ? value : [];
  });
  
  const storedDateRange = dateRange.getValue() || [null, null];
  const initialDateRange = [
    storedDateRange[0] ? new Date(storedDateRange[0]) : null,
    storedDateRange[1] ? new Date(storedDateRange[1]) : null
  ];
  const [dateRangeValue, setDateRangeValue] = useState(initialDateRange);
  
  const today = new Date();

  useEffect(() => {
    if (isOpen) {
      const value = type.getValue();
      if (typeof value === 'string') {
        setTypeValue(value.split(',').map(t => t.trim()).filter(Boolean));
      } else {
        setTypeValue(Array.isArray(value) ? value : []);
      }
      
      const storedDateRange = dateRange.getValue() || [null, null];
      const resetDateRange = [
        storedDateRange[0] ? new Date(storedDateRange[0]) : null,
        storedDateRange[1] ? new Date(storedDateRange[1]) : null
      ];
      setDateRangeValue(resetDateRange);
    }
  }, [isOpen, type, dateRange]);

  const submitFilters = () => {
    type.setValue(typeValue);
    const dateRangeForStorage = dateRangeValue[0] && dateRangeValue[1] ? [
      new Date(Date.UTC(
        dateRangeValue[0].getFullYear(),
        dateRangeValue[0].getMonth(),
        dateRangeValue[0].getDate(),
        0, 0, 0, 0
      )).toISOString(),
      new Date(Date.UTC(
        dateRangeValue[1].getFullYear(),
        dateRangeValue[1].getMonth(),
        dateRangeValue[1].getDate(),
        23, 59, 59, 999
      )).toISOString()
    ] : dateRangeValue;
    dateRange.setValue(dateRangeForStorage);
    setIsOpen(false);
  };

  const isCurrentStateDefault = () => {
    const isTypeDefault = !typeValue || typeValue.length === 0;
    const isDateRangeDefault = !dateRangeValue || !dateRangeValue[0] || !dateRangeValue[1];
    
    return isTypeDefault && isDateRangeDefault;
  };

  const shouldShowClearButton = useMemo(() => {
    const hasAppliedTypeFilter = type.getValue() && (
      (typeof type.getValue() === 'string' && type.getValue().length > 0) ||
      (Array.isArray(type.getValue()) && type.getValue().length > 0)
    );
    const hasAppliedDateFilter = dateRange.getValue() && Array.isArray(dateRange.getValue()) && 
      dateRange.getValue()[0] && dateRange.getValue()[1];
    
    const hasAppliedFilters = hasAppliedTypeFilter || hasAppliedDateFilter;
    
    const currentStateNotDefault = isOpen && !isCurrentStateDefault();
    
    return hasAppliedFilters || currentStateNotDefault;
  }, [type, dateRange, typeValue, dateRangeValue, isOpen]);

  return (
    <section className="d-flex align-items-center">
      <Dropdown isOpen={isOpen} toggle={() => setIsOpen(v => !v)} className="d-inline-block">
        <DropdownToggle className="btn btn-outline-primary d-flex align-items-center" tag="button">
          <i className="bx bx-filter-alt me-2"/> Add filter
        </DropdownToggle>
        <DropdownMenu className="filter-menu p-2 pt-3 rec-filter">
          <section className="d-flex justify-content-between gap-3">
            <FilterListWithInfinityScroll
              label="BY TYPE"
              filterState={typeValue}
              updateFilterState={setTypeValue}
              getData={() => Promise.resolve({ data: notificationTypes, pagination: { totalCount: notificationTypes.length } })}
              mapOptions={(types) => types.map(({ value, label }) => ({ id: value, label }))}
              showAllOption
            />
            <div className={styles.filterMenu}>
              <label className="mb-1 fw-bold">By Date Range</label>
              <DatePicker
                selectsRange
                startDate={dateRangeValue[0]}
                endDate={dateRangeValue[1]}
                onChange={update => {
                  if (update[0] && update[1]) {
                    const [start, end] = update;
                    const normalizedStart = new Date(start);
                    const normalizedEnd = new Date(end);
                    normalizedStart.setHours(0, 0, 0, 0);
                    normalizedEnd.setHours(23, 59, 59, 999);
                    setDateRangeValue([normalizedStart, normalizedEnd]);
                  } else {
                    setDateRangeValue(update);
                  }
                }}
                maxDate={today}
                isClearable
                className="form-control"
                placeholderText="Select date range"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                dateFormat="dd/MM/yyyy"
              />
              {dateRangeValue[0] && dateRangeValue[1] && (
                <div className="mt-2 text-muted small">
                  Selected: {formatDateRangeDisplay(dateRangeValue[0], dateRangeValue[1])}
                </div>
              )}
            </div>
          </section>
          <section className="w-100 px-2 pb-2 mt-3">
            <Button color="primary" onClick={submitFilters} className="w-100">
              Apply
            </Button>
          </section>
        </DropdownMenu>
      </Dropdown>
      {shouldShowClearButton &&
        <Button
          color="link"
          onClick={() => {
            setTypeValue([]);
            setDateRangeValue([null, null]);
            type.setValue();
            dateRange.setValue();
          }}
          className="text-danger no-border ms-2"
        >
          Clear filter
        </Button>
      }
    </section>
  );
};

export function TableHeader({ searchProvider, filterProvider, notificationTypes = [] }) {
  return (
    <section className={styles.tableHeaderContainer}>
      <div className={styles.filterSection}>
        <TableSearch
          className="notification-search"
          search={searchProvider?.getValue()}
          onSearch={searchProvider.setValue}
          placeholder="Search notifications..."
        />
        <TableFilter filterProvider={filterProvider} notificationTypes={notificationTypes} />
      </div>
    </section>
  );
}

export const SearchPlaceholder = () => (
  <>No matching notificaion.</>
);

export function NoNotificationsPlaceholder() {
  return (
    <div className="text-center">
      No notifications found.
    </div>
  );
}

export const columns = [
  {
    Header: "#",
    width: 50,
    className: "bg-white",
    Cell: (cellProps) => {
      const { params: { offset = 0 } } = useLocationQuery?.() || { params: { offset: 0 } };
      return (
        <label className={styles.serialNumber}>
          {cellProps.row.index + 1 + (offset ?? 0)}
        </label>
      );
    }
  },
  {
    Header: "Customer Name",
    accessor: "customerName",
    className: "bg-white",
    canSort: true,
    width: 160,
    Cell: ({ value }) => (
      <section>
        <div>{value}</div>
      </section>
    )
  },
  {
    Header: "Customer ID",
    accessor: "customerId",
    className: "bg-white",
    canSort: true,
    width: 120,
    Cell: ({ value }) => (
      <section>
        <div>{value}</div>
      </section>
    )
  },
  {
    Header: "Date & Time",
    accessor: "date",
    className: "bg-white",
    canSort: true,
    width: 150,
    Cell: ({ value }) => (
      <section>
        <div>{formatDateDDMMYYYY(value)}</div>
      </section>
    )
  },
  {
    Header: "Type",
    accessor: "type",
    className: "bg-white",
    canSort: true,
    width: 120,
    Cell: ({ value }) => (
      <section>
        <div>{value}</div>
      </section>
    )
  },
  {
    Header: "Title",
    accessor: "title",
    className: "bg-white",
    width: 200,
    Cell: ({ value }) => (
      <section className={styles.titleCell}>
        <div className={styles.titleText}>{value}</div>
      </section>
    )
  },
  {
    Header: "Message",
    accessor: "message",
    className: "bg-white",
    width: 250,
    Cell: ({ value }) => (
      <section>
        <div>{value}</div>
      </section>
    )
  }
];